/* src/pages/reports/clientes/AlumnosProyectoBarPage.tsx */
import { useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  TextField,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Typography,
  CircularProgress,
  Collapse,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useTranslation } from "react-i18next";
import { useAlumnosProyectoReport } from "../../../hooks/useReportJob";
import { useDistribucionAlumnosReport } from "../../../hooks/useReportJob";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#845EC2",
  "#4D8076",
  "#F65A83",
  "#2C73D2",
];

type RowProyecto = {
  proyecto: string;
  numAlu: number;
};

export default function AlumnosProyectoBarPage() {
  const { t } = useTranslation<"translation">();
  const today = dayjs().format("YYYY-MM-DD");
  const [desde, setDesde] = useState(today);
  const [hasta, setHasta] = useState(today);

  // ─────────────── Job 1: resumen proyectos (barras) ───────────────
  const { run, useStatus } = useAlumnosProyectoReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  // ─────────────── Job 2: alumnos del proyecto clicado ─────────────
  const { run: runAlumnos, useStatus: useStatusAlumnos } = useDistribucionAlumnosReport();
  const [jobIdAlu, setJobIdAlu] = useState<string>();
  const { data: statusAlu } = useStatusAlumnos(jobIdAlu);

  const [proyectoSel, setProyectoSel] = useState<string | null>(null);

  const lanzar = async () => {
    setProyectoSel(null); // reset de posible desplegable
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const rowsTop10: RowProyecto[] =
    (status?.result?.rows as RowProyecto[] | undefined)?.slice(0, 10) ?? [];

  const handleBarClick = (row: RowProyecto | undefined) => {
    if (!row?.proyecto) return;
    // Evitar doble clic mientras se está generando la lista
    if (statusAlu && !statusAlu.done && !statusAlu.failed) return;

    setProyectoSel(row.proyecto);
    runAlumnos({ campo: "proyecto", valor: row.proyecto, desde, hasta }).then(({ jobId }) =>
      setJobIdAlu(jobId)
    );
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const handleDownloadAlu = async () => {
    if (statusAlu?.result?.url) {
      try {
        await downloadReportFile(statusAlu.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("alumnosProyecto.titulo")}</Typography>

      {/* filtros */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          type="date"
          label={t("alumnosProyecto.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t("alumnosProyecto.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Button variant="contained" onClick={lanzar}>
          {t("alumnosProyecto.filtros.generar")}
        </Button>
      </Stack>

      {/* progreso (resumen) */}
      {status && !status.done && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t("alumnosProyecto.filtros.generar")} {status.progress}%
          </Typography>
        </Stack>
      )}

      {/* resultado (resumen + gráfico) */}
      {status?.done && status.result && (
        <>
          <Alert severity="success">
            {t("alumnosProyecto.estado.exito")}{" "}
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
            >
              {t("alumnosProyecto.estado.descargar")}
            </Button>
          </Alert>

          <Paper sx={{ p: 2, height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={rowsTop10} /* Top-10 */
                layout="vertical"
                margin={{ left: 120, right: 20 }}
              >
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="proyecto" width={140} />
                <Tooltip
                  // Cambia el nombre del dato mostrado en tooltip
                  formatter={(value: any) => [value, "nº students"]}
                />
                <Bar
                  dataKey="numAlu"
                  barSize={18}
                  isAnimationActive={false}
                  // Click en una barra ⇒ cargar alumnos del proyecto
                  onClick={(_, i) => handleBarClick(rowsTop10[i])}
                >
                  {rowsTop10.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} style={{ cursor: "pointer" }} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      {/* progreso (alumnos del proyecto) */}
      {proyectoSel && statusAlu && !statusAlu.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography>
            {/* Reutilizamos la key de la otra página */}
            {t("distribucionReservas.estado.generandoLista", {
              seg: proyectoSel,
            })}
          </Typography>
        </Stack>
      )}

      {/* desplegable con lista de alumnos del proyecto seleccionado */}
      <Collapse in={!!(statusAlu?.done && statusAlu.result)} unmountOnExit>
        {statusAlu?.result && (
          <>
            <Alert severity="info" sx={{ width: "fit-content" }}>
              XLSX:&nbsp;
              <Button
                variant="text"
                size="small"
                onClick={handleDownloadAlu}
                sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
              >
                {t("distribucionReservas.estado.descargar")}
              </Button>
            </Alert>

            <Paper sx={{ mt: 2, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("distribucionReservas.lista.titulo", { seg: proyectoSel })}
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("alumnos.nombre")}</TableCell>
                    <TableCell>{t("alumnos.email")}</TableCell>
                    <TableCell>{t("alumnos.telefono")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusAlu.result.rows.map((a: any) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.nombre}</TableCell>
                      <TableCell>{a.email}</TableCell>
                      <TableCell>{a.telefono}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </Collapse>
    </Stack>
  );
}
